/**
 * The brand filter's rules, decided away from React so they can be covered.
 *
 * Two sources, merged here:
 *
 *   radar `src/components/Shell.tsx` and `BrandFilter.tsx`: every prospects
 *   page shows some of the person's brands, all of them by default. The choice
 *   lives in the address (`?brand=all` or `?brand=a,b`), so a link from an
 *   email opens on the right brands, and is remembered per browser.
 *
 *   radar `src/lib/goji-frame.ts`: the workspace (this app's active brand) and
 *   the filter follow each other. The prospects service has brands this app
 *   does not (BetterEnrich, Catch-all Verifier, CosmeticComply,
 *   FaithScreener), so a service brand names the workspace brand it stands
 *   for with `goji_brand_id`. When the workspace changes the filter shows the
 *   brands linked to it, and picking exactly one linked brand on a page
 *   switches the workspace to its brand, as the frame did in both directions.
 *
 * Nothing here imports React or reads `window`.
 */

/** The two fields of a brand these rules read. */
export interface LinkedBrand {
  _id: string
  goji_brand_id?: string | null
}

/** What the filter wants: every brand, or these keys. */
export type BrandWant = string[] | 'all'

/** The browser's memory of the last choice. The key the old pages used on this same origin, so a choice made there carries over. */
export const BRAND_STORAGE_KEY = 'radar_brands'

/** `?brand=` read: `all`, a comma list of keys, or nothing usable. */
export function parseBrandParam(value: string | null | undefined): BrandWant | null {
  if (value === null || value === undefined || value === '') return null
  if (value === 'all') return 'all'
  const keys = value
    .split(',')
    .map((key) => key.trim())
    .filter((key) => key !== '')
  return keys.length > 0 ? keys : null
}

/** The filter as it is written into `?brand=` and onto every request. */
export function brandParamOf(want: BrandWant): string {
  return want === 'all' ? 'all' : want.join(',')
}

/**
 * The keys the filter holds, never empty once brands have loaded: what was
 * asked for, less what this person cannot see, or every brand when nothing
 * asked for is left.
 */
export function selectedKeys(want: BrandWant, shown: readonly { _id: string }[]): string[] {
  const every = shown.map((brand) => brand._id)
  if (want === 'all') return every
  const keep = want.filter((key) => every.includes(key))
  return keep.length > 0 ? keep : every
}

/**
 * A chip pressed. `single` pages (Keywords, a brand's voice) always pick
 * exactly one. Elsewhere the first press from All picks one, later presses add
 * or take away, and a choice of none or of every brand is All again.
 */
export function toggleBrand(key: string, state: { single: boolean; all: boolean; selected: readonly string[]; count: number }): BrandWant {
  if (state.single || state.all) return [key]
  const next = state.selected.includes(key) ? state.selected.filter((k) => k !== key) : [...state.selected, key]
  return next.length > 0 && next.length < state.count ? next : 'all'
}

/** The count a chip wears, clamped the way the old chip clamped it. */
export function chipCount(count: number | undefined): string | null {
  if (count === undefined || !Number.isFinite(count) || count <= 0) return null
  return count > 99 ? '99+' : String(Math.floor(count))
}

// ---------------------------------------------------------------------------
// The workspace, kept the same on both sides (radar lib/goji-frame.ts)
// ---------------------------------------------------------------------------

/** The service brands a workspace brand stands for. None for null (every brand) or a brand with nothing linked. */
export function keysForGoji(brands: readonly LinkedBrand[], gojiBrandId: string | null): string[] {
  if (gojiBrandId === null || gojiBrandId === '') return []
  return brands.filter((brand) => brand.goji_brand_id === gojiBrandId).map((brand) => brand._id)
}

/** The workspace brand a selection stands for: exactly one brand, linked to one. Null otherwise. */
export function gojiOf(brands: readonly LinkedBrand[], selected: readonly string[]): string | null {
  if (selected.length !== 1) return null
  return brands.find((brand) => brand._id === selected[0])?.goji_brand_id || null
}

/** A link that names exactly one brand (`?brand=x`), which wins over the workspace when the page opens. */
export function linkedBrand(param: string | null | undefined): string | null {
  const value = String(param ?? '').trim()
  return value !== '' && value !== 'all' && !value.includes(',') ? value : null
}

/**
 * What to do when the workspace names its brand.
 *
 *   follow  show these brands (`all` for every brand)
 *   tell    the page opened on a link to one brand, so the workspace switches
 *           to the brand it stands for instead
 *   keep    the page opened on a link to a brand this app does not have,
 *           which stays
 *
 * `initial` is true the first time the workspace is read on this page. After
 * that the workspace leads, whatever the address says.
 */
export function onWorkspaceBrand(
  brands: readonly LinkedBrand[],
  workspace: { id: string | null; initial: boolean },
  linked: string | null,
): { follow: BrandWant } | { tell: string } | { keep: true } {
  if (workspace.initial && linked !== null && brands.some((brand) => brand._id === linked)) {
    const goji = gojiOf(brands, [linked])
    if (goji === null) return { keep: true }
    if (goji !== workspace.id) return { tell: goji }
  }
  const keys = keysForGoji(brands, workspace.id)
  return { follow: keys.length > 0 ? keys : 'all' }
}

/**
 * Which service brands stand for the console's brand: those linked to it, or
 * `all` when no brand is chosen or none is linked. The sidebar's counts read
 * this, because they are drawn for the workspace rather than for a page.
 */
export function brandsForWorkspace(brands: readonly LinkedBrand[], gojiBrandId: string | null): string[] | 'all' {
  const keys = keysForGoji(brands, gojiBrandId)
  return keys.length > 0 ? keys : 'all'
}

/** The workspace brand as the filter reads it: none in the every-brand view, which shows every brand. */
export function workspaceBrandId(session: { superAdminView: boolean; activeBrandId: string | null }): string | null {
  return session.superAdminView ? null : session.activeBrandId
}
